import { createRouter, createWebHistory } from 'vue-router';
import GamesPage from '@/pages/GamesPage.vue';

const loadComponent = (importFn: () => Promise<any>) => {
    return () => {
        return importFn().catch((error) => {
            const knownErrors = [
                'Failed to fetch dynamically imported module',
                'Importing a module script failed',
                'Unable to preload CSS'
            ];

            if (knownErrors.some((e) => error.message?.includes(e))) {
                window.location.reload();
                return new Promise(() => {});
            }

            throw error;
        });
    };
};

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            name: 'mainPage',
            component: loadComponent(() => import('@/layout/MainLayout.vue')),
            meta: { requiresAuth: true },
            children: [
                {
                    path: '',
                    redirect: { name: 'dashboard' },
                },
                {
                    path: 'dashboard',
                    name: 'dashboard',
                    component: loadComponent(() => import('@/pages/DashboardPage.vue')),
                },
                {
                    path: 'schedule',
                    name: 'schedule',
                    component: loadComponent(() => import('@/pages/SchedulePage.vue')),
                },
                {
                    path: 'account',
                    name: 'account',
                    component: loadComponent(() => import('@/pages/AccountPage.vue')),
                },
                {
                    path: 'homework/:tab?',
                    name: 'homework',
                    component: loadComponent(() => import('@/pages/HomeworkPage.vue')),
                },
                {
                    path: 'games',
                    name: 'games-grid',
                    component: GamesPage,
                    children: [
                        {
                            path: ':trainerSlug',
                            name: 'game-view',
                            component: GamesPage,
                        }
                    ]
                },
                {
                    path: 'settings',
                    name: 'settings',
                    component: loadComponent(() => import('@/pages/SettingsPage.vue')),
                },
                {
                    path: 'additional-materials',
                    meta: { requiresAuth: true },
                    children: [
                        {
                            path: '',
                            name: 'additional-materials',
                            component: loadComponent(() => import('@/pages/additional-materials/Index.vue')),
                        },
                        {
                            path: ':id',
                            name: 'additional-materials-view',
                            component: loadComponent(() => import('@/pages/additional-materials/View.vue')),
                        },
                    ],
                },
            ],
        },
        {
            path: '/login',
            name: 'login',
            component: loadComponent(() => import('@/pages/LoginPage.vue')),
        },
        {
            path: '/reset-password',
            name: 'reset-password',
            component: loadComponent(() => import('@/pages/LoginPage.vue')),
        },
        {
            path: '/:catchAll(.*)',
            redirect: { name: 'login' },
        },
    ],
});

router.beforeEach((to, from, next) => {
    const token = localStorage.token;
    const requiresAuth = to.meta.requiresAuth;

    if (requiresAuth && !token) {
        next({ name: 'login' });
    }
    else if (token && (to.name === 'login' || to.name === 'reset-password')) {
        if (to.name === 'reset-password' && to.query.token) {
            next();
        } else {
            next({ name: 'dashboard' });
        }
    }
    else {
        if (to.name === 'reset-password' && !to.query.token && !token) {
            next({ name: 'login' });
        } else {
            next();
        }
    }
});

router.onError((error, to) => {
    const errors = [
        'Failed to fetch dynamically imported module',
        'Importing a module script failed',
        'Unable to preload CSS'
    ];

    if (errors.some((e) => error.message.includes(e))) {
        window.location.href = to.fullPath;
    }
});

export default router;